import React from 'react';
import classNames from 'classnames/bind';

import { coordinatesToPairs } from 'pipelines/components/ROICanvas/services/coordinates_to_pairs';
import { useCreateCameraSnapshot } from 'cameras/hooks/useCreateCameraSnapshot';
import { useCreateStreamSnapshot } from 'streams/hooks/useCreateStreamSnapshot';
import { useFile } from 'files/hooks/useFile';

import { IconButton } from 'components/IconButton/IconButtonDeprecated';

import { VideoSourceSelectLegacy } from 'components/VideoSourceSelect/VideoSourceSelectLegacy';
import { useDeploymentFormOptional } from 'deployments/components/Form/context';
import {
  ROICanvas,
  ROICanvasProps,
} from 'pipelines/components/ROICanvas/ROICanvas';
import { VideoSource } from 'hooks/api/useDeploymentsVideoSources';
import { VideoSourceSelection } from 'pipelines/services/rete/controls/video_source_selector';

import { getROIShapesFromInput } from './services/get_shapes_from_input';
import { VideoSourceROIPreview } from './Preview';
import styles from './VideoSourceROI.module.scss';

const c = classNames.bind(styles);

export type VideoSourceROIValue = {
  labels: string[] | null;
  coordinates: (readonly [number, number])[][] | null;
};

// FIXME: support for polygon and line strings
export type VideoSourceROIInputValue =
  | VideoSourceROIValue
  | {
      labels: string;
      coordinates: string;
    };

export type VideoSourceROIProps = Pick<ROICanvasProps, 'type'> & {
  value?: VideoSourceROIInputValue;
  onChange: (value: VideoSourceROIValue) => void;
};

export function VideoSourceROI({ type, value, onChange }: VideoSourceROIProps) {
  const { sources } = useDeploymentFormOptional();

  const [deploymentSource, setDeploymentSource] =
    React.useState<VideoSourceSelection | null>(null);
  const [source, setSource] = React.useState<VideoSource | null>(null);

  const internalValue = React.useMemo(
    () => getROIShapesFromInput(value, type),
    [value, type]
  );

  const {
    data: cameraSnapshotFile,
    mutate: createCameraSnapshot,
    isLoading: isCreatingCameraSnapshot,
    reset: resetCameraSnapshot,
  } = useCreateCameraSnapshot();
  const {
    data: streamSnapshotFile,
    mutate: createStreamSnapshot,
    isLoading: isCreatingStreamSnapshot,
    reset: resetStreamSnapshot,
  } = useCreateStreamSnapshot();

  const snapshotFileId =
    cameraSnapshotFile?.file_id ||
    streamSnapshotFile?.file_id ||
    source?.snapshot_file_id;

  const { data: snapshotFile } = useFile(snapshotFileId);

  const isStream =
    source !== undefined &&
    source !== null &&
    typeof source === 'object' &&
    'stream_type' in source;

  React.useEffect(() => {
    if (!sources || JSON.stringify(sources) === '{}') {
      return;
    }

    const [{ source_id, source_type }] = Object.values(sources);
    setDeploymentSource({ source_id, source_type });
    setSource(null);
  }, [sources]);

  function handleSelectionChange(selectedSource: VideoSource | null) {
    resetCameraSnapshot();
    resetStreamSnapshot();

    // Trigger snapshot if none exists
    // sources is undefined if this component is used in pipeline node editor properties
    if (
      selectedSource &&
      (!sources || typeof sources === 'object') &&
      !selectedSource.snapshot_file_id
    ) {
      if (
        'stream_type' in selectedSource &&
        (selectedSource.status === 'online' ||
          (selectedSource.status === 'unknown' &&
            selectedSource.stream_type === 'file'))
      ) {
        createStreamSnapshot({
          stream_id: selectedSource.id,
          gateway_id: selectedSource.gateway_id || undefined,
        });
      }

      if ('conn_type' in selectedSource && selectedSource.status === 'online') {
        createCameraSnapshot({
          camera_id: selectedSource.id,
          gateway_id: selectedSource.gateway_id || undefined,
        });
      }
    }

    setSource(selectedSource);
  }

  const handleROIChange = React.useCallback(
    (shapes: Record<string, number[]>) => {
      const labels = Object.keys(shapes);
      let coordinates = Object.values(shapes).map(coordinatesToPairs);

      // Close polygon
      if (type === 'polygon') {
        coordinates = coordinates.map((shape) => {
          const [start] = shape;
          return [...shape, start];
        });
      }

      onChange({ labels, coordinates });
    },
    [type, onChange]
  );

  function handleTakeSnapshot() {
    if (!source) {
      return;
    }

    if (source.status === 'online') {
      if (isStream) {
        resetCameraSnapshot();
        createStreamSnapshot({
          stream_id: source.id,
          gateway_id: source.gateway_id || undefined,
        });
        return;
      }

      resetStreamSnapshot();
      createCameraSnapshot({
        camera_id: source.id,
        gateway_id: source.gateway_id || undefined,
      });
    }
  }

  const isCreatingSnapshot =
    isCreatingCameraSnapshot || isCreatingStreamSnapshot;

  const isUploadingSnapshotFile =
    snapshotFile && snapshotFile.cloud_status === 'uploading';

  const isOffline = source && source.status !== 'online';
  const isFileStream = isStream && source && source.stream_type === 'file';

  return (
    <div className={c('wrap')}>
      <div className={c('source-select')}>
        <VideoSourceSelectLegacy
          onChange={handleSelectionChange}
          value={source ?? deploymentSource}
          size="small"
          isClearable
        />
      </div>

      <IconButton
        className={c('update-snapshot')}
        icon="take-snapshot"
        label="Take a new snapshot"
        onClick={handleTakeSnapshot}
        disabled={
          !source ||
          isCreatingSnapshot ||
          isUploadingSnapshotFile ||
          isOffline ||
          isFileStream
        }
      />

      <div className={c('roi')}>
        <ROICanvas
          type={type}
          onChange={handleROIChange}
          defaultValue={internalValue}
        >
          <VideoSourceROIPreview fileId={snapshotFileId} />
        </ROICanvas>
      </div>
    </div>
  );
}
